// IE Hub - Edit Company Dialog Component
// Allows editing a company's account number

import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Backdrop,
  useMediaQuery,
  useTheme,
  Slide,
} from "../ui";
import CloseIcon from "@mui/icons-material/Close";
import type { Company } from "@/pages/private/dashboard/SharePage";
import { debtEndpoints, sharePageEndpoints } from "@/shared/api/endpoints";
import {
  ApiResponse,
  CustomerSharingResponse,
  PaymentOfferRecord,
} from "@/shared/api/types";
import { httpGet, httpPut } from "@/shared/api/http";
import { useAlert } from "../app/providers/AlertProvider";
import { UI_MESSAGES } from "../constants/ui";
import React from "react";
import { DialogTitle } from "@ui";
import { ThemeColours } from "@/clients/acme/config/colours";
import SpecialTextField from "@/ui/fields/SpecialTextField";
import { useReferenceIdHelp } from "@/components/referenceId";
import { InputAdornment, MenuItem, Select, TextField } from "@mui/material";
import { DebtsService } from "@/shared/api/services";
import { ordinal } from "@/shared/share/sharePageUtils";

interface EditCompanyDialogProps {
  open: boolean;
  onClose: () => void;
  company: Company;
  setCompanies: React.Dispatch<React.SetStateAction<Company[]>>;
  setOpenDeleteDialog: (open: boolean) => void;
  companies?: Company[];
  debts?: any[];
  setDebts?: React.Dispatch<React.SetStateAction<any[]>>;
}

const Transition = React.forwardRef<
  unknown,
  { in?: boolean; children: React.ReactElement }
>(function Transition(props, ref) {
  return <Slide direction="up" ref={ref as React.Ref<unknown>} {...props} />;
});

const EditCompanyDialog: React.FC<EditCompanyDialogProps> = ({
  open,
  onClose,
  company,
  setCompanies,
  setOpenDeleteDialog,
  companies,
  debts = [],
  setDebts,
}) => {
  const { showAlert } = useAlert();
  const [paymentOfferRecord, setPaymentOfferRecord] =
    useState<PaymentOfferRecord>({} as PaymentOfferRecord);

  const [coveringNotes, setCoveringNotes] = useState("");

  const [editedAccountNumber, setEditedAccountNumber] = useState(
    company.reference
  );
  const [noReferenceId, setNoReferenceId] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const linkedDebt = React.useMemo(() => {
    // Primary lookup: match by explicit debtId stored on the share row
    if (company?.raw?.debtId) {
      const byId = debts?.find((d: any) => d.id === company.raw?.debtId);
      if (byId) return byId;
    }

    // Fallback for referred/profile companies whose share row pre-dates the debtId field:
    // match by source (customer_profile / referred) + creditor name so that editing the
    // reference from the Share page still syncs the debt record.
    const rawSource = typeof (company?.raw as any)?.source === "string"
      ? ((company?.raw as any).source as string).trim().toLowerCase()
      : "";
    const isReferralSource = rawSource === "customer_profile" || rawSource === "referred";
    if (isReferralSource && debts && debts.length > 0) {
      const normalize = (v: unknown): string =>
        typeof v === "string" ? v.trim().toLowerCase() : "";
      const lenderName = normalize((company?.raw as any)?.lenderName);
      return (
        debts.find(
          (d: any) =>
            (normalize(d.source) === "customer_profile" ||
              normalize(d.source) === "referred") &&
            (lenderName
              ? normalize(d.creditor) === lenderName ||
                normalize(d.lenderName) === lenderName
              : true)
        ) || null
      );
    }

    return null;
  }, [company, debts]);

  const isCreditCard = React.useMemo(() => {
    if (!linkedDebt) return false;
    return (
      linkedDebt.accTypeCode === "ZC" ||
      linkedDebt.debtType === "creditCard" ||
      linkedDebt.category === "creditCards"
    );
  }, [linkedDebt]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string
    );

    setEditedAccountNumber(company.reference ?? "");
    setCoveringNotes((company.raw as any)?.notes || "");
    if (company.reference == customerData.emailAddress) {
      setNoReferenceId(true);
      setEditedAccountNumber("");
    } else {
      setNoReferenceId(false);
    }
    setErrors({});
  }, [company, open]);

  useEffect(() => {
    if (!open || company.isFp) {
      return;
    }

    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string
    );

    const lenderName = company.raw?.lenderName;
    const accountNumber = company.raw?.accountNumber;
    const customerId = customerData.customerId;

    async function main() {
      const response = await httpGet<ApiResponse>(
        debtEndpoints.paymentOffer.get(
          customerId,
          lenderName as string,
          accountNumber as string
        )
      );
      const data: any = response?.data;

      if (data.length > 0) {
        setPaymentOfferRecord(data[0]);
      } else {
        setPaymentOfferRecord({} as PaymentOfferRecord);
      }
    }

    main();
  }, [company, open]);

  const [isSaving, setIsSaving] = useState(false);

  const {
    helpDialog: editReferenceHelpDialog,
    helpLinkText: editReferenceHelpLinkText,
    noValueCheckboxLabel: editNoValueCheckboxLabel,
    label: editReferenceLabel,
  } = useReferenceIdHelp({ companyName: company?.name });

  const onDelete = () => {
    onClose();
    setOpenDeleteDialog(true);
  };

  const handleSave = async () => {
    if (company.isFp && coveringNotes.length == 0) {
      showAlert({
        severity: "warning",
        message: `Please enter a covering note.`,
        autoHide: true,
        duration: 5000,
      });
      return;
    }

    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string
    );

    if (!noReferenceId) {
      const referenceValue = (editedAccountNumber || "").trim();
      const hasNonDigits = /\D/.test(referenceValue);

      if (referenceValue.length === 0) {
        setErrors((prev) => ({
          ...prev,
          referenceNumber: "Reference number is required",
        }));
        showAlert({
          severity: "error",
          message: `Please enter the Reference number for the company ${company.name}.`,
          autoHide: true,
          duration: 3000,
        });
        return;
      }

      if (isCreditCard && (referenceValue.length !== 4 || hasNonDigits)) {
        setErrors((prev) => ({
          ...prev,
          referenceNumber: UI_MESSAGES.CREDIT_CARD_4_DIGIT_ALERT,
        }));
        showAlert({
          severity: "warning",
          message: UI_MESSAGES.CREDIT_CARD_4_DIGIT_ALERT,
          autoHide: true,
          duration: 3000,
        });
        return;
      }

      if (editedAccountNumber.includes("'")) {
        showAlert({
          severity: "warning",
          message: "Please enter a valid reference number.",
          autoHide: true,
          duration: 2500,
        });
        return;
      }
    }

    let newAccNumber = editedAccountNumber;

    if (noReferenceId) {
      newAccNumber = customerData.emailAddress;
    }

    // Match AddCompanyForm: block only when the same reference is reused for the
    // same company/creditor row (not across different companies).
    if (companies) {
      const comparisonCompanyName = company.name;
      const productName =
        typeof company.raw?.companyName === "string"
          ? company.raw.companyName
          : "";
      for (const c of companies) {
        if (c.raw?.id === company.raw?.id) {
          continue;
        }
        if (c.reference != newAccNumber) {
          continue;
        }
        if (
          comparisonCompanyName == c.name ||
          (productName.length > 0 && productName == c.name)
        ) {
          showAlert({
            severity: "warning",
            message: `A company with the same reference number already exists`,
            autoHide: true,
            duration: 2000,
          });

          return;
        }
      }
    }

    setIsSaving(true);
    try {
      const payload: any = {
        ...company.raw,
      };

      payload[company.isFp ? "referenceNumber" : "accountNumber"] =
        newAccNumber;

      if (company.isFp) {
        payload.notes = coveringNotes;
      }

      // Ensure "editable" is not sent to the API as the backend model does not support it
      const sanitizedPayload = { ...payload };
      delete sanitizedPayload.editable;

      if (company.isFp) {
        await httpPut<CustomerSharingResponse>(
          sharePageEndpoints.createFreepostSharing(),
          sanitizedPayload
        );
      } else {
        await httpPut<CustomerSharingResponse>(
          sharePageEndpoints.createCustomerSharing(),
          sanitizedPayload
        );
      }

      const newObj: Company = {
        ...company,
        reference: newAccNumber,
        raw: {
          ...company.raw,
          ...(company.isFp
            ? { referenceNumber: newAccNumber }
            : { accountNumber: newAccNumber }),
        } as CustomerSharingResponse,
      };

      if (coveringNotes.length > 0) {
        (newObj.raw as any).notes = coveringNotes;
      }

      // update payment offer if needed
      if (Object.keys(paymentOfferRecord).length != 0) {
        const payload = {
          ...paymentOfferRecord,
          paymentOfferAmount: paymentOfferRecord.paymentOfferAmount
            ? Number(paymentOfferRecord.paymentOfferAmount)
            : null,
        };

        await DebtsService.updatePaymentOffer(Number(paymentOfferRecord.id), payload, {
          skipErrorCapture: true,
        });

        const { paymentOfferAmount, paymentPreferredFrequency } =
          paymentOfferRecord;

        newObj.paymentOfferData = {
          paymentOfferAmount: paymentOfferAmount
            ? Number(paymentOfferAmount)
            : "",
          paymentPreferredFrequency: ordinal(
            parseInt(paymentPreferredFrequency || "0")
          ),
        };
      }

      // write back to the linked debt if it exists
      if (linkedDebt) {
        const debtPayload = {
          ...linkedDebt,
          accountNumber: newAccNumber,
        };
        await DebtsService.updateDebt(debtPayload, { skipErrorCapture: true });

        if (setDebts) {
          setDebts((prev) =>
            prev.map((d) => (d.id === linkedDebt.id ? { ...d, accountNumber: newAccNumber } : d))
          );
        }

        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("iehub-debts-changed", {
              detail: { debtId: linkedDebt.id },
            })
          );
        }
      }

      setCompanies((prev) =>
        prev.map((c) => (c.id === company.id ? newObj : c))
      );

      onClose();
    } catch (error) {
      console.error("Error updating company:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog data-testid="components-edit-company-dialog-modal-action"
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          ...(isMobile && {
            margin: 0,
            width: "100%",
            maxWidth: "100%",
            maxHeight: "90vh",
            borderRadius: "16px 16px 0 0",
            position: "fixed",
            bottom: 0,
          }),
          ...(!isMobile && {
            maxHeight: "100vh",
            padding: "0px 0px 0px 0px",
          }),
        },
      }}
      TransitionComponent={Transition as any}
      BackdropComponent={Backdrop}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        },
      }}
    >
      <Box data-testid="components-edit-company-dialog-container-action-1" sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Dialog Title */}
        <DialogTitle data-testid="components-edit-company-dialog-header-action"
          sx={{
            position: "relative",
            px: 4,
            justifyContent: "space-between",
            display: "flex",
            alignItems: "center",
            // Divider inset 16px from each edge so it doesn't touch the border (June Patch #4)
            "&::after": {
              content: '""',
              position: "absolute",
              left: "16px",
              right: "16px",
              bottom: 0,
              borderBottom: "1px solid #e0e0e0",
            },
          }}
        >
          <Typography data-testid="components-edit-company-dialog-text-edit-company" variant="h5" sx={{ fontWeight: 600 }}>
            Edit Company
          </Typography>

          <IconButton data-testid="components-edit-company-dialog-btn-on-close"
            aria-label="close"
            onClick={onClose}
            sx={{
              color: "#9ca3af",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, px: 2, flexGrow: 1, overflowY: "auto" }}>
          <Box data-testid="components-edit-company-dialog-container-action-2" sx={{ display: "flex", flexDirection: "column" }}>
            <Box data-testid="components-edit-company-dialog-container-action-3"
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "0px",
                pt: "16.152px",
                pb: "20px",
                px: 2,
              }}
            >
              <Box data-testid="components-edit-company-dialog-container-action-4">
                <Typography data-testid="components-edit-company-dialog-text-company-name"
                  variant="h6"
                  sx={{ fontWeight: 500, mb: 0, color: ThemeColours.label }}
                >
                  Company name
                </Typography>
                <TextField data-testid="components-edit-company-dialog-input-action-1" value={company.name} disabled fullWidth />
              </Box>

              <SpecialTextField
                isAccountField={true}
                isCreditCardAccount={isCreditCard}
                digitsOnly={isCreditCard}
                label={editReferenceLabel}
                placeholder="Enter your account number"
                noValueChecked={noReferenceId}
                onNoValueChange={(checked) => {
                  setNoReferenceId(checked);
                  if (errors.referenceNumber) {
                    setErrors((prev) => {
                      const copy = { ...prev };
                      delete copy.referenceNumber;
                      return copy;
                    });
                  }
                }}
                value={editedAccountNumber}
                onChange={(e) => {
                  let val = e.target.value;
                  if (isCreditCard) {
                    val = val.replace(/'/g, "").replace(/\D/g, "").slice(0, 4);
                  }
                  setEditedAccountNumber(val);
                  if (errors.referenceNumber) {
                    setErrors((prev) => {
                      const copy = { ...prev };
                      delete copy.referenceNumber;
                      return copy;
                    });
                  }
                }}
                error={!!errors.referenceNumber}
                helperText={errors.referenceNumber}
                validation={{
                  required: !noReferenceId,
                  maxLength: isCreditCard ? 4 : 50,
                }}
                allowNoValue={true}
                noValueCheckboxLabel={editNoValueCheckboxLabel}
                noValueFieldName="noReferenceId"
                helpLinkText={editReferenceHelpLinkText}
                helpDialog={editReferenceHelpDialog}
                showErrorBelow={true}
              />

              {company.isFp &&
                !(company.raw?.lastSharedDate || company.raw?.submitDate) && (
                  <Box data-testid="components-edit-company-dialog-container-action-5" sx={{ mt: 2 }}>
                    <Typography data-testid="components-edit-company-dialog-text-covering-note"
                      variant="h6"
                      sx={{ fontWeight: 500, mb: 0, color: ThemeColours.label }}
                    >
                      Covering note
                    </Typography>
                    <TextField data-testid="components-edit-company-dialog-input-action-2"
                      value={coveringNotes}
                      onKeyDown={(e) => {
                        if (
                          coveringNotes.length >= 500 &&
                          e.key.length === 1 &&
                          !e.ctrlKey &&
                          !e.metaKey
                        ) {
                          showAlert({
                            severity: "warning",
                            message: UI_MESSAGES.CHARACTER_LIMIT_REACHED,
                          });
                        }
                      }}
                      onChange={(e) => setCoveringNotes(e.target.value)}
                      fullWidth
                      multiline
                      rows={4}
                      InputProps={{
                        style: {
                          backgroundColor: "white",
                        },
                      }}
                      inputProps={{
                        maxLength: 500,
                        onPaste: (e) => {
                          const pastedText = e.clipboardData.getData("text");
                          const selectionStart = (e.target as any).selectionStart || 0;
                          const selectionEnd = (e.target as any).selectionEnd || 0;
                          if (
                            coveringNotes.length -
                              (selectionEnd - selectionStart) +
                              pastedText.length >
                            500
                          ) {
                            showAlert({
                              severity: "warning",
                              message: UI_MESSAGES.CHARACTER_LIMIT_REACHED,
                            });
                          }
                        },
                      }}
                    />
                  </Box>
                )}

              {Object.keys(paymentOfferRecord).length != 0 && (
                <Box data-testid="components-edit-company-dialog-container-action-6" sx={{ mt: 2 }}>
                  <Typography data-testid="components-edit-company-dialog-text-i-would-like-to-make-a-payment-offer-of"
                    variant="h6"
                    sx={{ fontWeight: 500, mb: 0.8, color: ThemeColours.label }}
                  >
                    I would like to make a payment offer of
                  </Typography>
                  <TextField data-testid="components-edit-company-dialog-input-action-3"
                    fullWidth
                    value={paymentOfferRecord.paymentOfferAmount ?? ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "" || /^\d*\.?\d{0,2}$/.test(val)) {
                        setPaymentOfferRecord({
                          ...paymentOfferRecord,
                          paymentOfferAmount: val === "" ? null : (val as any),
                        });
                      }
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1,
                        maxHeight: 45,
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">£</InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">/month</InputAdornment>
                      ),
                      style: {
                        backgroundColor: "white",
                      },
                    }}
                  />
                  <Typography data-testid="components-edit-company-dialog-text-preferred-payment-day"
                    variant="h6"
                    sx={{
                      fontWeight: 500,
                      mb: 0.8,
                      color: ThemeColours.label,
                      fontSize: 16,
                    }}
                  >
                    Preferred payment day
                  </Typography>
                  <Select data-testid="components-edit-company-dialog-select-action"
                    sx={{ backgroundColor: "#fff", mb: 1.5, maxHeight: 45 }}
                    fullWidth
                    value={paymentOfferRecord.paymentPreferredFrequency ?? null}
                    onChange={(e) =>
                      setPaymentOfferRecord({
                        ...paymentOfferRecord,
                        paymentPreferredFrequency: e.target.value as string,
                      })
                    }
                  >
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((n) => (
                      <MenuItem data-testid="components-edit-company-dialog-menu-item-action" key={n} value={n}>
                        {n}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              )}

              <Box data-testid="components-edit-company-dialog-container-action-7"
                sx={{
                  mt: 3,
                  display: "flex",
                  justifyContent: !isMobile ? "flex-end" : "space-between",
                  gap: 2,
                }}
              >
                <Button data-testid="components-edit-company-dialog-btn-on-delete"
                  onClick={onDelete}
                  sx={{
                    height: "45px",
                    borderRadius: "10px",
                    color: "#FF0F17",
                    fontSize: "18px",
                    fontWeight: 600,
                    textTransform: "none",
                    border: "1px solid #d5d5d5",
                    "&:hover": {
                      backgroundColor: "#e5e7eb",
                      border: "none",
                    },
                    "&:disabled": {
                      backgroundColor: "#f3f4f6",
                      color: "#9ca3af",
                    },
                    minWidth: !isMobile ? 200 : "auto",
                    flex: isMobile ? 1 : "unset",
                  }}
                >
                  Delete company
                </Button>
                <Button data-testid="components-edit-company-dialog-btn-handle-save"
                  onClick={handleSave}
                  disabled={isSaving}
                  sx={{
                    height: "45px",
                    backgroundColor: "#D4E03B",
                    borderRadius: "10px",
                    color: "#003838",
                    fontSize: "18px",
                    fontWeight: 600,
                    textTransform: "none",
                    border: "none",
                    "&:hover": {
                      backgroundColor: "#e6f332ff",
                      border: "none",
                    },
                    "&:disabled": {
                      backgroundColor: "#d5e03b64",
                      color: "#0038384f",
                    },
                    minWidth: !isMobile ? 200 : "auto",
                    flex: isMobile ? 1 : "unset",
                  }}
                >
                  {isSaving ? "Saving..." : "Save"}
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Box>
    </Dialog>
  );
};

export default EditCompanyDialog;
